import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle2, ShieldCheck, Lock, FileCheck } from "lucide-react";
import Logo from "../components/marketing/Logo";
import PasswordStrength from "../components/auth/PasswordStrength";
import api, { formatApiError } from "../lib/api";
import { track, EVT } from "../lib/analytics";

const FIRM_TYPES = [
  { value: "ifa", label: "IFA / Financial Advisor" },
  { value: "mortgage_broker", label: "Mortgage Broker" },
  { value: "insurance_broker", label: "Insurance Broker" },
  { value: "fintech", label: "Fintech Company" },
  { value: "consumer_credit", label: "Consumer Credit Firm" },
  { value: "law_firm", label: "Law Firm (FCA Regulated)" },
  { value: "other", label: "Other FCA Regulated Business" },
];

const FIRM_SIZES = [
  { value: "sole", label: "Just me" },
  { value: "2-5", label: "2 – 5 advisors" },
  { value: "6-15", label: "6 – 15 advisors" },
  { value: "16-50", label: "16 – 50 advisors" },
  { value: "50+", label: "50+ advisors" },
];

export default function SignUp() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState(params.get("plan") || "practitioner");
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  useEffect(() => {
    const p = params.get("plan");
    if (p && ["starter", "practitioner", "professional", "enterprise"].includes(p)) setSelectedPlan(p);
  }, [params]);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    firm_name: "", fca_number: "", firm_type: "ifa", firm_size: "sole",
    password: "", confirm_password: "",
    agree_terms: false, confirm_fca_authorised: false, marketing_opt_in: false,
  });
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [devLink, setDevLink] = useState("");

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password !== form.confirm_password) {
      setErr("Passwords don't match"); return;
    }
    if (!form.agree_terms || !form.confirm_fca_authorised) {
      setErr("Please confirm the terms and your FCA authorisation"); return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/api/auth/register", {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone || null,
        firm_name: form.firm_name,
        fca_number: form.fca_number,
        firm_type: form.firm_type,
        firm_size: form.firm_size,
        password: form.password,
        agree_terms: form.agree_terms,
        confirm_fca_authorised: form.confirm_fca_authorised,
        marketing_opt_in: form.marketing_opt_in,
      });
      setSubmitted(true);
      setDevLink(data.verification_link_dev || "");
      track(EVT.user_signed_up, {
        firm_type: form.firm_type, firm_size: form.firm_size,
        selected_plan: selectedPlan, billing_period: billingPeriod,
      });
      track(EVT.trial_started, { plan: selectedPlan });
      // Persist the selected plan so we kick off checkout after first login.
      try { localStorage.setItem("compliche_pending_plan", JSON.stringify({ plan: selectedPlan, billing_period: billingPeriod })); } catch {}
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center px-6 py-12">
        <div className="card2 p-10 max-w-md w-full text-center animate-slide-up" data-testid="signup-success">
          <div className="w-16 h-16 rounded-full bg-emerald2-50 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 size={32} className="text-emerald2-dark" />
          </div>
          <h2 className="t-h1 mb-3">Check your email</h2>
          <p className="t-body text-slate2 mb-6">
            We've sent a verification link to <strong className="text-ink">{form.email}</strong>. Click it to activate your account and start your 14-day free trial.
          </p>
          {devLink && (
            <div className="mb-6 p-3 rounded-md text-left t-body-sm" style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" }}>
              <strong>Dev mode link:</strong>
              <a href={devLink} className="block t-mono mt-1 break-all text-indigo2 hover:underline">{devLink}</a>
            </div>
          )}
          <Link to="/login" className="btn btn-secondary btn-block">Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr]" data-testid="signup-page">
      {/* Left panel — dark navy with benefits */}
      <aside className="hidden lg:flex bg-navy-900 text-white flex-col p-10 relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-60" />
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="relative">
          <Link to="/"><Logo variant="light" size="lg" /></Link>
          <div className="mt-16">
            <h2 className="t-display leading-tight">
              Start your <span style={{ color: "#10B981" }}>14-day</span> free trial.
            </h2>
            <p className="t-body-lg text-white/65 mt-4">
              Join FCA-regulated firms already proving compliance with Compliche.
            </p>
          </div>

          <ul className="mt-12 space-y-5">
            {[
              { icon: CheckCircle2, label: "Set up in under 10 minutes" },
              { icon: ShieldCheck, label: "FCA, ASA & ICO compliance checking built in" },
              { icon: Lock, label: "Immutable audit trail from day one" },
              { icon: FileCheck, label: "Cancel anytime · no credit card required" },
            ].map((b) => (
              <li key={b.label} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "rgba(16,185,129,0.12)" }}>
                  <b.icon size={16} className="text-emerald2" />
                </div>
                <span className="t-body-lg text-white/85">{b.label}</span>
              </li>
            ))}
          </ul>

          <div className="mt-16 p-5 rounded-xl border border-white/10" style={{ background: "rgba(255,255,255,0.03)" }}>
            <div className="flex gap-1 mb-3 text-amber2">
              {Array.from({ length: 5 }).map((_, j) => <span key={j}>★</span>)}
            </div>
            <p className="t-body text-white/85 italic mb-4">"Compliche replaced four compliance tools and saved 11 hours every week. The audit trail alone is worth the price."</p>
            <div className="t-caption text-white/55">— Compliance Director, Mid-sized IFA</div>
          </div>
        </div>
      </aside>

      {/* Right form */}
      <main className="bg-white px-6 sm:px-10 lg:px-16 py-10 lg:py-16 overflow-y-auto">
        <div className="lg:hidden mb-8"><Logo variant="dark" /></div>
        <div className="max-w-xl">
          <h1 className="t-h1 mb-2">Create your account</h1>
          <p className="t-body text-slate2 mb-8">No credit card required. Cancel anytime.</p>

          {err && (
            <div className="mb-5 p-3 rounded-md text-sm flex items-start gap-2" style={{ background: "rgba(239,68,68,0.06)", color: "#991B1B", border: "1px solid #FECACA" }} data-testid="signup-error">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />{err}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label2">First name<span className="req">*</span></label>
                <input className="input2" required value={form.first_name} onChange={upd("first_name")} data-testid="signup-firstname" />
              </div>
              <div>
                <label className="label2">Last name<span className="req">*</span></label>
                <input className="input2" required value={form.last_name} onChange={upd("last_name")} data-testid="signup-lastname" />
              </div>
            </div>

            <div>
              <label className="label2">Work email<span className="req">*</span></label>
              <input type="email" required className="input2" value={form.email} onChange={upd("email")} data-testid="signup-email" />
            </div>

            <div>
              <label className="label2">Phone (optional, for SMS 2FA backup)</label>
              <input type="tel" className="input2" placeholder="+44 7..." value={form.phone} onChange={upd("phone")} data-testid="signup-phone" />
            </div>

            <div className="pt-4 mt-4 border-t border-border2">
              <div className="t-label text-slate2 mb-3">Firm details</div>
            </div>

            <div>
              <label className="label2">Firm name<span className="req">*</span></label>
              <input className="input2" required value={form.firm_name} onChange={upd("firm_name")} data-testid="signup-firmname" />
            </div>

            <div>
              <label className="label2">FCA registration number (FRN)<span className="req">*</span></label>
              <input
                className="input2 t-mono"
                required
                placeholder="e.g. 123456"
                pattern="\d{4,7}"
                title="4 to 7 digits"
                value={form.fca_number}
                onChange={upd("fca_number")}
                data-testid="signup-fca"
              />
              <div className="help2">Find yours at <a href="https://register.fca.org.uk" target="_blank" rel="noopener noreferrer" className="text-indigo2 hover:underline">register.fca.org.uk</a></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label2">Firm type<span className="req">*</span></label>
                <select className="input2" value={form.firm_type} onChange={upd("firm_type")} data-testid="signup-firmtype">
                  {FIRM_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label2">Firm size<span className="req">*</span></label>
                <select className="input2" value={form.firm_size} onChange={upd("firm_size")} data-testid="signup-firmsize">
                  {FIRM_SIZES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            <div className="pt-4 mt-4 border-t border-border2">
              <div className="t-label text-slate2 mb-3">Account security</div>
            </div>

            <div>
              <label className="label2">Password<span className="req">*</span></label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  required
                  className="input2 pr-12"
                  value={form.password}
                  onChange={upd("password")}
                  data-testid="signup-password"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate2 hover:text-ink" data-testid="signup-toggle-pw">
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <PasswordStrength password={form.password} />
            </div>

            <div>
              <label className="label2">Confirm password<span className="req">*</span></label>
              <input type={showPw ? "text" : "password"} required className="input2" value={form.confirm_password} onChange={upd("confirm_password")} data-testid="signup-confirm" />
            </div>

            <div className="pt-4 space-y-3">
              <label className="flex items-start gap-2.5 t-body-sm cursor-pointer">
                <input type="checkbox" required checked={form.agree_terms} onChange={upd("agree_terms")} className="w-4 h-4 mt-0.5 accent-indigo2 flex-shrink-0" data-testid="signup-terms" />
                <span>I agree to the <a href="#" className="text-indigo2 hover:underline">Terms of Service</a> and <a href="#" className="text-indigo2 hover:underline">Privacy Policy</a><span className="req">*</span></span>
              </label>
              <label className="flex items-start gap-2.5 t-body-sm cursor-pointer">
                <input type="checkbox" required checked={form.confirm_fca_authorised} onChange={upd("confirm_fca_authorised")} className="w-4 h-4 mt-0.5 accent-indigo2 flex-shrink-0" data-testid="signup-fca-confirm" />
                <span>I confirm this firm is FCA-authorised or an appointed representative<span className="req">*</span></span>
              </label>
              <label className="flex items-start gap-2.5 t-body-sm cursor-pointer">
                <input type="checkbox" checked={form.marketing_opt_in} onChange={upd("marketing_opt_in")} className="w-4 h-4 mt-0.5 accent-indigo2 flex-shrink-0" data-testid="signup-marketing" />
                <span>Send me FCA compliance updates and product news</span>
              </label>
            </div>

            <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={loading} data-testid="signup-submit">
              {loading ? <><Loader2 size={16} className="animate-spin" />Creating account…</> : "Create account & start trial"}
            </button>

            <p className="text-center t-body-sm text-slate2 mt-5">
              Already have an account? <Link to="/login" className="text-indigo2 font-medium hover:underline">Sign in →</Link>
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
